import { BrandLogo } from '@/components/brand/BrandLogo';
import { useState, type FormEvent } from 'react';
import { t } from '@/i18n';
import { ApiError } from '@/api/client';
import { useLogin } from '@/api/hooks/auth';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';

/**
 * Вход за офиса. За управител, диспечер и счетоводство MFA е задължителна
 * (ADR 0005) — полето за код се показва чак когато сървърът го поиска.
 */
export function LoginPage() {
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');

  const error = login.error;
  const needsMfa =
    error instanceof ApiError && (error.code === 'MFA_REQUIRED' || error.code === 'MFA_INVALID');
  const message = error instanceof ApiError ? error.message : error ? t.auth.failed : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate({ email, password, ...(totp ? { totp } : {}) });
  };

  return (
    <div className="flex h-full items-center justify-center bg-ground bg-[radial-gradient(60%_50%_at_50%_30%,rgba(147,255,20,0.07),transparent_70%)] p-6">
      <form onSubmit={submit} className="kmdx-card w-full max-w-sm p-7 shadow-[0_30px_80px_-40px_rgba(147,255,20,0.35)]">
        <div className="mb-7 flex flex-col items-center text-center">
          <BrandLogo size="lg" tagline />
          <div className="mt-4 text-xs uppercase tracking-[0.2em] text-ink-faint">{t.app.subtitle}</div>
        </div>

        <div className="space-y-3">
          <Field label={t.auth.email} required>
            <Input
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="off"
              enterKeyHint="next"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label={t.auth.password} required>
            <Input
              type="password"
              autoComplete="current-password"
              enterKeyHint="go"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          {needsMfa ? (
            <Field label={t.auth.totp} hint={t.auth.mfaRequired} required>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                enterKeyHint="go"
                maxLength={6}
                className="font-mono tabular tracking-[0.4em]"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
            </Field>
          ) : null}
        </div>

        {message ? (
          <p className="mt-3 rounded border border-danger/40 bg-danger-soft px-2 py-1.5 text-xs text-danger">
            {message}
          </p>
        ) : null}

        <Button type="submit" variant="primary" className="mt-5 w-full" loading={login.isPending}>
          {t.auth.signIn}
        </Button>
      </form>
    </div>
  );
}
